import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { untilDestroyed } from '@ngneat/until-destroy';
import { OfferType } from 'app/modules/main/models/offers';
import { Offer } from 'app/modules/main/models/offers';
import { SelectableData } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataColumn } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableDataColumnExtractor } from 'app/modules/main/components/data-selector/data-selector.models';
import { AccountDatePipe } from 'app/modules/main/pipes/account-date-pipe';
import { combineLatest } from 'rxjs';
import { of } from 'rxjs';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { first } from 'rxjs/operators';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class OfferColumnExtractor implements SelectableDataColumnExtractor<Offer> {

  // select control column width is 13%
  //  -- all columns should add up to 100%
  readonly columns: SelectableDataColumn[] = [
    {
      headingLabel: $localize`:@@communication-centre.advertiser.offer-selector.column.type.label:Type`,
      headingStyle: { width: '14%' },
      valueStyle: { width: '14%' },
    },
    {
      headingLabel: $localize`:@@communication-centre.advertiser.offer-selector.column.title.label:Title`,
      headingStyle: { width: '20%' },
      valueStyle: { width: '20%' },
    },
    {
      headingLabel: $localize`:@@communication-centre.advertiser.offer-selector.column.description.label:Description`,
      headingStyle: { width: '30%' },
      valueStyle: { width: '30%' },
    },
    {
      headingLabel: $localize`:@@communication-centre.advertiser.offer-selector.column.start-end.label:Starts - Ends`,
      headingStyle: { width: '23%' },
      valueStyle: { width: '23%', whiteSpace: 'pre-wrap' },
    },
  ];

  constructor(
    private readonly accountDatePipe: AccountDatePipe,
  ) {
  }

  getValue(
    index: number,
    selectableOffer: SelectableData<Offer>
  ): Observable<string> {

    const offer = selectableOffer.data;

    switch (index) {

      case 0:
        return of(this.localizeOfferType(offer.type));

      case 1:
        return of(offer.title || '');

      case 2:
        return of(offer.description);

      case 3:

        return combineLatest([
          this.accountDatePipe.transform(offer.startFrom, 'shortDate'),
          this.accountDatePipe.transform(offer.startFrom, 'shortTime'),
          this.accountDatePipe.transform(offer.endAfter, 'shortDate'),
          this.accountDatePipe.transform(offer.endAfter, 'shortTime'),
        ])
          .pipe(
            untilDestroyed(this),
            first(),
            map(
              ([startFromDate, startFromTime, endAfterDate, endAfterTime]) =>
                `${startFromDate} ${startFromTime}\n${endAfterDate} ${endAfterTime}`
            ),
          );

      default:
        return of('');
    }
  }

  private localizeOfferType(
    type: OfferType
  ) {

    if (type === OfferType.VOUCHER) {
      return $localize`:@@communication-centre.advertiser.offer-selector.offer.type.voucher:Voucher`;

    } else if (type === OfferType.PROMOTION) {
      return $localize`:@@communication-centre.advertiser.offer-selector.offer.type.promotion:Promotion`;

    } else {
      return $localize`:@@communication-centre.advertiser.offer-selector.offer.type.unknown:Unknown`;
    }
  }

}
